import { useState, type ReactNode } from "react";
import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/lib/authContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Bell, SlidersHorizontal, Tags, ChevronDown, ChevronRight, Trash2, Plus, Mail, Image as ImageIcon, Loader2 } from "lucide-react";
import { api, Tag, StatusTagRule, WorkflowPipeline, getNotificationPrefs, setNotificationPref, type NotificationType, emailSignatureApi, backupRunsApi, type BackupRun } from "@/lib/api";
import { backupStaleness, formatAge } from "@/lib/backupStaleness";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useUserPref, useTimeFormat, PREF_SIDEBAR_AUTO_COLLAPSE, PREF_HELP_AUTO_DISMISS } from "@/lib/userPrefs";
import { TagChip, TagPicker } from "@/components/custom/TagPicker";

// ─── Notification type metadata ───────────────────────────────────────────────

const NOTIF_TYPES: {
  type: NotificationType;
  label: string;
  description: string;
  emailSupported: boolean;
}[] = [
  {
    type: "task_assigned",
    label: "Task assigned to me",
    description: "When someone assigns a task to you.",
    emailSupported: true,
  },
  {
    type: "task_comment",
    label: "New comment on a task",
    description: "When someone comments on a task you are assigned to.",
    emailSupported: true,
  },
  {
    // NOTE: the server emits "status_changed" (fireStatusNotification) — this row
    // historically saved under "stage_changed", which nothing emits, so the
    // toggle was a no-op. Migration 096 carried existing prefs over.
    type: "status_changed",
    label: "Project status changed",
    description: "When a project's workflow status advances or is updated (role-based email).",
    emailSupported: true,
  },
  {
    type: "task_unblocked",
    label: "It's your turn on a task",
    description: "When the work before yours is completed and your task is ready to start.",
    emailSupported: true,
  },
  {
    type: "project_assigned",
    label: "Added to a client",
    description: "When you are added as a team member to a client.",
    emailSupported: true,
  },
  {
    type: "project_completed",
    label: "Project completed",
    description: "When a project is marked as complete on a client you are assigned to.",
    emailSupported: true,
  },
  {
    type: "mention",
    label: "Mentioned (@mention)",
    description: "When someone @mentions you in team chat.",
    emailSupported: true,
  },
  {
    type: "project_comment",
    label: "New message in team chat",
    description: "When someone posts in the team chat for a client you are assigned to.",
    emailSupported: true,
  },
  // NOTE: "client_message" was removed — the server never emits it (the real
  // type for portal chat is "client_chat_message", which has its own row
  // below). Migration 096 carried any saved prefs over.
  {
    type: "client_document_upload",
    label: "Client uploads a document",
    description: "When a client uploads a file via the client portal.",
    emailSupported: true,
  },
  {
    type: "client_task_complete",
    label: "Client completes a portal task",
    description: "When a client marks a task as done in the client portal.",
    emailSupported: true,
  },
  {
    type: "project_ready_for_review",
    label: "Project ready for review",
    description: "When a project is marked ready for review.",
    emailSupported: true,
  },
  {
    type: "client_document_deleted",
    label: "Client deletes an uploaded document",
    description: "When a client removes a file they previously uploaded.",
    emailSupported: true,
  },
  {
    type: "mention_on_note",
    label: "Mentioned on a client note",
    description: "When you are @mentioned in a client note.",
    emailSupported: true,
  },
  {
    type: "client_chat_message",
    label: "Client sends a chat message",
    description: "When a client sends a message in the client portal chat.",
    emailSupported: true,
  },
  {
    type: "client_task_comment",
    label: "Client comments on a task",
    description: "When a portal client posts a comment on a task.",
    emailSupported: true,
  },
  {
    type: "email_assigned",
    label: "Email assigned to me",
    description: "When a teammate assigns a shared-inbox email to you.",
    emailSupported: true,
  },
  {
    type: "feedback_resolved",
    label: "My feedback item is resolved",
    description: "When a Feedback & Ideas item you submitted is marked done.",
    emailSupported: true,
  },
  {
    type: "document_delete_requested",
    label: "Document delete requested",
    description: "When a teammate asks to delete a client document (managers and admins).",
    emailSupported: true,
  },
  {
    type: "document_deleted",
    label: "Document deleted",
    description: "When a client document is deleted (managers and admins).",
    emailSupported: true,
  },
  {
    type: "document_delete_decided",
    label: "My delete request was decided",
    description: "When a manager or admin approves or rejects a delete request you filed.",
    emailSupported: true,
  },
];

function NotificationPrefsCard() {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: prefs = [], isLoading } = useQuery({
    queryKey: ["notification-prefs"],
    queryFn: getNotificationPrefs,
    staleTime: 60_000,
  });

  // Build a map for quick lookup; missing = use defaults (in_app:true, email:false)
  const prefMap = Object.fromEntries(prefs.map((p) => [p.notification_type, p]));

  const getInApp = (type: NotificationType) => prefMap[type]?.in_app ?? true;
  const getEmail = (type: NotificationType) => prefMap[type]?.email_enabled ?? false;

  const saveMutation = useMutation({
    mutationFn: ({ type, in_app, email_enabled }: { type: NotificationType; in_app: boolean; email_enabled: boolean }) =>
      setNotificationPref(type, in_app, email_enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notification-prefs"] }),
    onError: () => toast({ title: "Failed to save preference", variant: "destructive" }),
  });

  const toggle = (type: NotificationType, field: "in_app" | "email_enabled", value: boolean) => {
    const current = prefMap[type];
    saveMutation.mutate({
      type,
      in_app: field === "in_app" ? value : (current?.in_app ?? true),
      email_enabled: field === "email_enabled" ? value : (current?.email_enabled ?? false),
    });
  };

  return (
    <Card id="notification-prefs">
      <CardHeader
        className="cursor-pointer select-none py-4"
        onClick={() => setOpen((v) => !v)}
      >
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" />
          <span className="flex-1">Notification Preferences</span>
          {open
            ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
            : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        </CardTitle>
        {open && (
          <CardDescription className="mt-1.5">
            Choose which in-app notifications you receive for each event type.
          </CardDescription>
        )}
      </CardHeader>
      {open && <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            {/* Header row */}
            <div className="grid grid-cols-[1fr_80px_80px] gap-2 px-4 py-2 bg-muted/50 border-b">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Event</span>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center">In-App</span>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center">Email</span>
            </div>
            {NOTIF_TYPES.map((n, i) => (
              <div
                key={n.type}
                className={`grid grid-cols-[1fr_80px_80px] gap-2 px-4 py-3 items-center ${i < NOTIF_TYPES.length - 1 ? "border-b" : ""}`}
              >
                <div>
                  <p className="text-sm font-medium">{n.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.description}</p>
                </div>
                <div className="flex justify-center">
                  <Switch
                    checked={getInApp(n.type)}
                    onCheckedChange={(v) => toggle(n.type, "in_app", v)}
                    disabled={saveMutation.isPending}
                  />
                </div>
                <div className="flex justify-center">
                  {n.emailSupported ? (
                    <Switch
                      checked={getEmail(n.type)}
                      onCheckedChange={(v) => toggle(n.type, "email_enabled", v)}
                      disabled={saveMutation.isPending}
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground/50">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>}
    </Card>
  );
}

// ─── Email signature ───────────────────────────────────────────────────────────

const DEFAULT_SIGNATURE_STARTER = `<table cellpadding="0" cellspacing="0" style="font-family: Arial, sans-serif; font-size: 13px; color: #333;">
  <tr><td><b>{{name}}</b></td></tr>
  <tr><td>{{title}}</td></tr>
  <tr><td>Carpenter &amp; Company, CPAs, PC</td></tr>
  <tr><td>{{phone}}</td></tr>
  <tr><td><a href="https://carpentercpas.com">carpentercpas.com</a></td></tr>
</table>`;

function EmailSignatureCard() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState<"html" | "text">("html");
  const [html, setHtml] = useState("");
  const [text, setText] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [preview, setPreview] = useState("");
  const [title, setTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const { data: saved, isLoading } = useQuery({
    queryKey: ["email-signature"],
    queryFn: () => emailSignatureApi.get(),
    staleTime: 60_000,
    enabled: open,
  });

  if (saved && !loaded) {
    setHtml(saved.email_signature_html ?? "");
    setText(saved.email_signature_text ?? "");
    setEnabled(saved.email_signature_enabled ?? true);
    // The PUT save stores exactly one of html/text (the other nulled) based on the
    // mode picked here — infer the mode back from what's saved so reopening Settings
    // doesn't silently switch a plain-text signature back to (empty) HTML mode and
    // wipe it on the next save.
    setMode(saved.email_signature_html?.trim() ? "html" : saved.email_signature_text?.trim() ? "text" : "html");
    setLoaded(true);
  }

  // Live preview re-sanitizes server-side on change/blur — the sanitizer is deliberately
  // server-only (never shipped to the client), so this shows the true rendered result.
  const runPreview = async (value: string) => {
    if (!value.trim()) { setPreview(""); return; }
    try {
      const res = await emailSignatureApi.preview(value);
      setPreview(res.html);
    } catch {
      // preview failures are non-fatal — the save button re-sanitizes anyway
    }
  };

  const saveMutation = useMutation({
    mutationFn: () => emailSignatureApi.save({ html: mode === "html" ? html : null, text: mode === "text" ? text : null, enabled }),
    onSuccess: (res) => {
      toast({ title: "Signature saved" });
      setHtml(res.email_signature_html ?? html);
    },
    onError: (err: any) => toast({ title: "Failed to save signature", description: err.message, variant: "destructive" }),
  });

  const startFromTemplate = () => {
    const rendered = DEFAULT_SIGNATURE_STARTER
      .replace(/\{\{name\}\}/g, user?.name ?? "")
      .replace(/\{\{title\}\}/g, title)
      .replace(/\{\{phone\}\}/g, phone);
    setHtml(rendered);
    void runPreview(rendered);
  };

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await emailSignatureApi.uploadAsset(file);
      const next = `${html}\n<img src="${url}" alt="Logo">`;
      setHtml(next);
      void runPreview(next);
      toast({ title: "Image added" });
    } catch (err: any) {
      toast({ title: "Failed to upload image", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card id="email-signature">
      <CardHeader className="cursor-pointer select-none py-4" onClick={() => setOpen((v) => !v)}>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          <span className="flex-1">Email signature</span>
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        </CardTitle>
        {open && (
          <CardDescription className="mt-1.5">
            Appended automatically to email you send, reply to, and forward from CentCom.
          </CardDescription>
        )}
      </CardHeader>
      {open && (
        <CardContent className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Add my signature to outgoing email</Label>
                  <p className="text-xs text-muted-foreground">Turn off to send with no signature at all.</p>
                </div>
                <Switch checked={enabled} onCheckedChange={setEnabled} />
              </div>

              <div className="flex gap-2">
                <Button type="button" size="sm" variant={mode === "html" ? "default" : "outline"} onClick={() => setMode("html")}>HTML</Button>
                <Button type="button" size="sm" variant={mode === "text" ? "default" : "outline"} onClick={() => setMode("text")}>Plain text</Button>
              </div>

              {mode === "html" ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Title (for the template)" value={title} onChange={(e) => setTitle(e.target.value)} className="text-sm" />
                    <Input placeholder="Phone (for the template)" value={phone} onChange={(e) => setPhone(e.target.value)} className="text-sm" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={startFromTemplate}>Start from firm template</Button>
                    <label className="inline-flex">
                      <input type="file" accept="image/png,image/jpeg,image/gif" className="hidden" disabled={uploading}
                        onChange={(e) => handleUpload(e.target.files?.[0] ?? null)} />
                      <Button type="button" size="sm" variant="outline" disabled={uploading} asChild>
                        <span>
                          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5 mr-1" />}
                          Add logo/image
                        </span>
                      </Button>
                    </label>
                  </div>
                  <Textarea
                    value={html}
                    onChange={(e) => { setHtml(e.target.value); void runPreview(e.target.value); }}
                    onBlur={() => runPreview(html)}
                    className="min-h-[160px] font-mono text-xs"
                    placeholder="<p>Your name<br>Carpenter &amp; Company, CPAs, PC</p>"
                  />
                  <div>
                    <Label className="text-xs text-muted-foreground">Preview</Label>
                    <div
                      className="border rounded-md p-3 mt-1 bg-muted/30 text-sm min-h-[60px]"
                      dangerouslySetInnerHTML={{ __html: preview || "<span style=\"color:#999\">Nothing to preview yet</span>" }}
                    />
                  </div>
                </div>
              ) : (
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="min-h-[120px] text-sm"
                  placeholder={`Your name\nCarpenter & Company, CPAs, PC`}
                />
              )}

              <div className="flex justify-end">
                <Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Saving…" : "Save signature"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

const BACKUP_JOBS = [
  "centcom-db-dump",
  "centcom-manifest",
  "centcom-metadata-backfill",
  "centcom-trash-purge",
  "centcom-nas-copy",
  "centcom-reconcile",
];

function backupDetailSummary(run: BackupRun | undefined): string {
  if (!run) return "No runs recorded yet";
  const d = run.details || {};
  const msg = (d.error ?? d.warning) as string | undefined;
  if (msg) return String(msg);
  const bits: string[] = [];
  if (typeof d.bytes === "number") bits.push(`${(d.bytes / 1048576).toFixed(1)} MB`);
  if (typeof d.documents === "number") bits.push(`${d.documents} docs`);
  if (typeof d.key === "string") bits.push(d.key);
  return bits.join(" · ") || "-";
}

function BackupsCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const canSeeBackups = user?.role === "admin" || user?.role === "manager";
  const { data, isLoading, error } = useQuery({
    queryKey: ["backup-runs"],
    queryFn: () => backupRunsApi.list(),
    staleTime: 60_000,
    enabled: open && canSeeBackups,
  });
  if (!canSeeBackups) return null;
  const now = new Date();
  const byJob = new Map((data?.latest ?? []).map((r) => [r.job, r]));
  const jobs = Array.from(new Set([...BACKUP_JOBS, ...byJob.keys()]));
  return (
    <Card id="backups">
      <CardHeader className="cursor-pointer select-none py-4" onClick={() => setOpen((v) => !v)}>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          <span className="flex-1">Backups</span>
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        </CardTitle>
        {open && <CardDescription className="mt-1.5">Admin and manager. Red = last run failed or is overdue (26 h for nightly jobs, 8 days for the weekly reconcile).</CardDescription>}
      </CardHeader>
      {open && (
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : error ? (
            <p className="text-sm text-destructive">{(error as Error).message}</p>
          ) : (
            <div className="divide-y rounded-md border">
              {jobs.map((job) => {
                const run = byJob.get(job);
                const { stale, ageMs } = backupStaleness(job, run?.finished_at, now);
                const bad = !run || run.status === "error" || stale;
                return (
                  <div key={job} className={`flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm ${bad ? "bg-destructive/10" : ""}`}>
                    <span className="font-mono w-56 shrink-0">{job}</span>
                    <Badge variant={bad ? "destructive" : run?.status === "warning" ? "secondary" : "outline"}>
                      {run ? run.status : "no runs"}
                    </Badge>
                    <span className={bad ? "text-destructive" : "text-muted-foreground"}>{formatAge(ageMs)}</span>
                    <span className="text-muted-foreground truncate flex-1 min-w-0" title={backupDetailSummary(run)}>{backupDetailSummary(run)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function AdminSignatureDefaultCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [template, setTemplate] = useState("");
  const [preview, setPreview] = useState("");
  const [loaded, setLoaded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["email-signature-default"],
    queryFn: () => emailSignatureApi.getDefaultTemplate(),
    staleTime: 60_000,
    enabled: open,
  });

  if (data && !loaded) {
    setTemplate(data.template_html ?? "");
    setLoaded(true);
  }

  const runPreview = async (value: string) => {
    if (!value.trim()) { setPreview(""); return; }
    try {
      const res = await emailSignatureApi.preview(value);
      setPreview(res.html);
    } catch {
      // non-fatal
    }
  };

  const saveMutation = useMutation({
    mutationFn: () => emailSignatureApi.saveDefaultTemplate(template),
    onSuccess: (res) => { toast({ title: "Firm template saved" }); setTemplate(res.template_html); },
    onError: (err: any) => toast({ title: "Failed to save", description: err.message, variant: "destructive" }),
  });

  if (user?.role !== "admin") return null;

  return (
    <Card id="firm-signature-template">
      <CardHeader className="cursor-pointer select-none py-4" onClick={() => setOpen((v) => !v)}>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          <span className="flex-1">Firm signature template</span>
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        </CardTitle>
        {open && <CardDescription className="mt-1.5">Admin only. Staff can use this as a starting point via "Start from firm template."</CardDescription>}
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <>
              <Textarea
                value={template}
                onChange={(e) => { setTemplate(e.target.value); void runPreview(e.target.value); }}
                onBlur={() => runPreview(template)}
                className="min-h-[160px] font-mono text-xs"
                placeholder="Use {{name}}, {{title}}, {{phone}}, {{email}} as placeholders"
              />
              <div>
                <Label className="text-xs text-muted-foreground">Preview</Label>
                <div
                  className="border rounded-md p-3 mt-1 bg-muted/30 text-sm min-h-[60px]"
                  dangerouslySetInnerHTML={{ __html: preview || "<span style=\"color:#999\">Nothing to preview yet</span>" }}
                />
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Saving…" : "Save firm template"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ─── Collapsible section wrapper ──────────────────────────────────────────────

function CollapsibleCard({
  title,
  description,
  icon,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card className={className}>
      <CardHeader
        className="cursor-pointer select-none py-4"
        onClick={() => setOpen((v) => !v)}
      >
        <CardTitle className="flex items-center gap-2">
          {icon}
          <span className="flex-1">{title}</span>
          {open
            ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
            : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
        </CardTitle>
        {open && description && (
          <CardDescription className="mt-1.5">{description}</CardDescription>
        )}
      </CardHeader>
      {open && children}
    </Card>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [sidebarAutoCollapse, setSidebarAutoCollapse] = useUserPref(PREF_SIDEBAR_AUTO_COLLAPSE, true);
  const [helpAutoDismiss, setHelpAutoDismiss] = useUserPref(PREF_HELP_AUTO_DISMISS, true);
  const [timeFmt, setTimeFmt] = useTimeFormat();

  const [expandedPipelineId, setExpandedPipelineId] = useState<string | null>(null);
  const [expandedStatusId, setExpandedStatusId] = useState<string | null>(null);

  const { data: workflowPipelines = [] } = useQuery<WorkflowPipeline[]>({
    queryKey: ["workflow-pipelines"],
    queryFn: () => api.getWorkflowPipelines(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: tagRulesForStatus = [] } = useQuery<StatusTagRule[]>({
    queryKey: ["status-tag-rules", expandedStatusId],
    queryFn: () => api.getStatusTagRules(expandedStatusId!),
    enabled: !!expandedStatusId,
  });

  const { data: allTags = [] } = useQuery<Tag[]>({
    queryKey: ["tags"],
    queryFn: () => api.getTags(),
  });

  const addTagRuleMutation = useMutation({
    mutationFn: ({ statusId, tagId, action }: { statusId: string; tagId: string; action: "add" | "remove" }) =>
      api.addStatusTagRule(statusId, tagId, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["status-tag-rules", expandedStatusId] });
      toast({ title: "Tag rule added" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteTagRuleMutation = useMutation({
    mutationFn: ({ statusId, ruleId }: { statusId: string; ruleId: string }) =>
      api.deleteStatusTagRule(statusId, ruleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["status-tag-rules", expandedStatusId] }),
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-8">
        <div>
           <h1 className="text-3xl font-heading font-bold tracking-tight mb-2">Settings</h1>
           <p className="text-muted-foreground">Manage your preferences and application configuration.</p>
        </div>

        {/* ── General ── */}
        <CollapsibleCard
          className="border-l-4 border-l-sidebar-primary shadow-md"
          icon={<Shield className="w-5 h-5 text-sidebar-primary" />}
          title="Current User"
          description="Your logged-in profile information."
        >
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Active User Profile</Label>
              <div className="flex items-center gap-3 p-3 border rounded-md bg-muted/30">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold">
                  {user?.avatar || "U"}
                </div>
                <div>
                  <p className="font-medium text-sm" data-testid="text-user-name">{user?.name || "User"}</p>
                  <p className="text-xs text-muted-foreground capitalize" data-testid="text-user-role">{user?.role || "staff"}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </CollapsibleCard>

        <CollapsibleCard
          className="border-l-4 border-l-primary shadow-md"
          icon={<SlidersHorizontal className="w-5 h-5 text-primary" />}
          title="My Preferences"
          description="Personal display settings — saved per device, not synced."
        >
          <CardContent className="space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Auto-collapse sidebar on navigation</Label>
                <p className="text-xs text-muted-foreground">
                  When on, the sidebar automatically collapses each time you navigate to a new page.
                  Turn off to keep it always visible.
                </p>
              </div>
              <Switch
                checked={sidebarAutoCollapse}
                onCheckedChange={setSidebarAutoCollapse}
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Auto-dismiss help panel on outside click</Label>
                <p className="text-xs text-muted-foreground">
                  When on, clicking anywhere outside the Help panel closes it. Turn off to keep it pinned
                  by default — you can also toggle the pin button inside the panel itself.
                </p>
              </div>
              <Switch
                checked={helpAutoDismiss}
                onCheckedChange={setHelpAutoDismiss}
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Show time as hours &amp; minutes</Label>
                <p className="text-xs text-muted-foreground">
                  When on, time values display as hours and minutes (e.g. 1h 30m).
                  When off, decimal format is used (e.g. 1.50h).
                </p>
              </div>
              <Switch
                checked={timeFmt === "hm"}
                onCheckedChange={(v) => setTimeFmt(v ? "hm" : "decimal")}
              />
            </div>
          </CardContent>
        </CollapsibleCard>

        <NotificationPrefsCard />

        <EmailSignatureCard />

        <AdminSignatureDefaultCard />

        <BackupsCard />

        <CollapsibleCard
          icon={<Tags className="w-5 h-5 text-primary" />}
          title="Tag Rules"
          description={`Tags that are automatically added or removed when a project enters a workflow status. ${user?.role !== "admin" ? "Contact an admin to modify rules." : "Expand a pipeline, then a status to manage its rules."}`}
        >
          <CardContent className="space-y-2">
            {workflowPipelines.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pipelines configured.</p>
            ) : (
              workflowPipelines.map((pipeline) => (
                <div key={pipeline.id} className="border rounded-md overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors text-left"
                    onClick={() => setExpandedPipelineId(expandedPipelineId === pipeline.id ? null : pipeline.id)}
                  >
                    <span>{pipeline.name}</span>
                    {expandedPipelineId === pipeline.id
                      ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </button>
                  {expandedPipelineId === pipeline.id && (
                    <div className="border-t divide-y">
                      {(pipeline.stages ?? []).map((status: any) => (
                        <div key={status.id} className="bg-muted/20">
                          <button
                            className="w-full flex items-center justify-between px-6 py-2 text-xs hover:bg-muted/40 transition-colors text-left"
                            onClick={() => setExpandedStatusId(expandedStatusId === status.id ? null : status.id)}
                          >
                            <div className="flex items-center gap-2">
                              <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: status.color }} />
                              <span className="font-medium">{status.name}</span>
                              <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">{status.status_type}</Badge>
                            </div>
                            {expandedStatusId === status.id
                              ? <ChevronDown className="w-3 h-3 text-muted-foreground" />
                              : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                          </button>
                          {expandedStatusId === status.id && (
                            <div className="px-6 pb-3 pt-1 space-y-3 bg-background">
                              {tagRulesForStatus.length > 0 && (
                                <div className="space-y-1">
                                  {tagRulesForStatus.map((rule) => {
                                    const tag = allTags.find(t => t.id === rule.tag_id);
                                    return (
                                      <div key={rule.id} className="flex items-center gap-2">
                                        <Badge variant={rule.action === "add" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0 h-5 shrink-0">
                                          {rule.action === "add" ? "Add" : "Remove"}
                                        </Badge>
                                        {tag && <TagChip tag={tag} />}
                                        {!tag && <span className="text-xs text-muted-foreground">Unknown tag</span>}
                                        {user?.role === "admin" && (
                                          <button onClick={() => deleteTagRuleMutation.mutate({ statusId: status.id, ruleId: rule.id })} className="ml-auto text-muted-foreground hover:text-destructive transition-colors">
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {tagRulesForStatus.length === 0 && <p className="text-xs text-muted-foreground italic">No rules yet.</p>}
                              {user?.role === "admin" && (
                                <AddTagRuleRow
                                  statusId={status.id}
                                  allTags={allTags}
                                  onAdd={(tagId, action) => addTagRuleMutation.mutate({ statusId: status.id, tagId, action })}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </CollapsibleCard>
      </div>
    </Layout>
  );
}

function AddTagRuleRow({
  statusId,
  allTags,
  onAdd,
}: {
  statusId: string;
  allTags: Tag[];
  onAdd: (tagId: string, action: "add" | "remove") => void;
}) {
  const [selectedTagId, setSelectedTagId] = useState("");
  const [action, setAction] = useState<"add" | "remove">("add");

  const handleAdd = () => {
    if (!selectedTagId) return;
    onAdd(selectedTagId, action);
    setSelectedTagId("");
  };

  return (
    <div className="flex items-center gap-2">
      <Select value={selectedTagId} onValueChange={setSelectedTagId}>
        <SelectTrigger className="h-7 text-xs flex-1">
          <SelectValue placeholder="Pick a tag…" />
        </SelectTrigger>
        <SelectContent>
          {allTags.map((tag) => (
            <SelectItem key={tag.id} value={tag.id}>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                {tag.name}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={action} onValueChange={(v) => setAction(v as "add" | "remove")}>
        <SelectTrigger className="h-7 text-xs w-24 shrink-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="add">Add</SelectItem>
          <SelectItem value="remove">Remove</SelectItem>
        </SelectContent>
      </Select>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs px-2 shrink-0"
        disabled={!selectedTagId}
        onClick={handleAdd}
      >
        <Plus className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}
